import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, AlertCircle, Trash2 } from 'lucide-react';
import { useReportLink, useReport, useDeleteReportLink } from '@/hooks/useReport';
import { ReportKpiHeader } from '@/components/report/ReportKpiHeader';
import { ReportTrendChart } from '@/components/report/ReportTrendChart';
import { ReportTable } from '@/components/report/ReportTable';
import { ReportSection } from '@/components/report/ReportSection';
import { ReportUnderperformers } from '@/components/report/ReportUnderperformers';
import { ReportExecutionsData } from '@/components/report/ReportExecutionsData';
import { ReportLineItemPerformance } from '@/components/report/ReportLineItemPerformance';
import {
  insertionOrderColumns,
  lineItemColumns,
  domainColumns,
  topUrlColumns,
  urlMetricColumns,
} from '@/components/report/report-columns';
import { formatDuration } from '@/lib/report-format';

/**
 * Report dashboard for an execution. Resolves the linked reportId, downloads the
 * report payload, and renders the monitoring pipeline outputs:
 *   - KPI header (campaign_summary)
 *   - trend chart (daily_metrics)
 *   - comparison tables (IO / line item / domain / top URLs)
 *   - watchlist (underperformers)
 *   - lazy drill-down (url_metrics, executions_data)
 */
export function ReportDashboardView() {
  const { executionId } = useParams<{ executionId: string }>();
  const navigate = useNavigate();

  const { data: linkData, isLoading: linkLoading } = useReportLink(executionId || '');
  const reportId = linkData?.link?.reportId ?? '';
  const { data: report, isLoading: reportLoading, error } = useReport(reportId);
  const deleteLink = useDeleteReportLink(executionId || '');

  const back = () => navigate(`/campaigns/${executionId}`);

  // --- Loading & error states ---

  if (linkLoading || (reportId && reportLoading)) {
    return (
      <CenteredState>
        <Loader2 size={28} className="animate-spin text-adv-accent" />
        <p className="adv-state__desc">Loading report…</p>
      </CenteredState>
    );
  }

  if (!linkLoading && !reportId) {
    return (
      <CenteredState>
        <div className="adv-state__icon mb-0">
          <AlertCircle size={26} />
        </div>
        <p className="adv-state__desc">No report is linked to this execution.</p>
        <button onClick={back} className="adv-btn adv-btn--secondary adv-btn--sm">
          Back to execution
        </button>
      </CenteredState>
    );
  }

  if (error || !report) {
    return (
      <CenteredState>
        <AlertCircle size={28} className="text-adv-warning" />
        <p className="text-adv-body-m text-adv-primary">
          Could not load report{' '}
          <code className="adv-num text-adv-code-xs bg-adv-sunken px-1 rounded-adv-sm">{reportId}</code>.
        </p>
        <p className="text-adv-body-s text-adv-secondary max-w-md text-center">
          {error instanceof Error ? error.message : 'The report file may be missing or invalid.'}
        </p>
        <div className="flex items-center gap-3">
          <button onClick={back} className="adv-btn adv-btn--secondary adv-btn--sm">
            Back to execution
          </button>
          <button
            onClick={() => deleteLink.mutate(undefined, { onSuccess: back })}
            className="adv-btn adv-btn--danger adv-btn--sm"
          >
            <Trash2 size={14} /> Unlink report
          </button>
        </div>
      </CenteredState>
    );
  }

  const outputs = report.outputs;
  const summary = outputs.campaign_summary?.[0];

  return (
    <div className="max-w-7xl mx-auto px-3 sm:px-4 py-4 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-3 min-w-0">
          <button
            onClick={back}
            className="adv-btn adv-btn--ghost adv-btn--icon"
            title="Back to execution"
          >
            <ArrowLeft size={18} />
          </button>
          <div className="min-w-0">
            <h1 className="adv-page-title truncate">
              Report Dashboard
            </h1>
            <p className="text-adv-body-s text-adv-secondary truncate">
              <span className="adv-num">{executionId}</span> · report{' '}
              <span className="adv-num">{reportId}</span>
              {report.durationMs != null && (
                <span className="adv-num">{` · ${formatDuration(report.durationMs)}`}</span>
              )}
            </p>
          </div>
        </div>
      </div>

      {/* KPI header */}
      {summary && (
        <ReportSection title="Campaign summary">
          <ReportKpiHeader summary={summary} />
        </ReportSection>
      )}

      {/* Trend */}
      {outputs.daily_metrics && outputs.daily_metrics.length > 0 && (
        <ReportSection title="Daily trend" subtitle={`${outputs.daily_metrics.length} days`}>
          <ReportTrendChart daily={outputs.daily_metrics} />
        </ReportSection>
      )}

      {/* Top domains & URLs — expanded, right under the trend */}
      {outputs.domain_metrics && outputs.domain_metrics.length > 0 && (
        <ReportSection title="Top domains" count={outputs.domain_metrics.length}>
          <ReportTable rows={outputs.domain_metrics} columns={domainColumns} initialLimit={15} />
        </ReportSection>
      )}

      {outputs.top_urls && outputs.top_urls.length > 0 && (
        <ReportSection title="Top URLs" count={outputs.top_urls.length}>
          <ReportTable rows={outputs.top_urls} columns={topUrlColumns} initialLimit={15} />
        </ReportSection>
      )}

      {/* Underperformers / watchlist — collapsed */}
      {outputs.underperformers && outputs.underperformers.length > 0 && (
        <ReportSection
          title="Underperformers"
          count={outputs.underperformers.length}
          collapsible
          defaultOpen={false}
        >
          <ReportUnderperformers rows={outputs.underperformers} />
        </ReportSection>
      )}

      {/* Comparison tables — expanded */}
      {outputs.insertion_order_metrics && outputs.insertion_order_metrics.length > 0 && (
        <ReportSection title="Insertion orders" count={outputs.insertion_order_metrics.length}>
          <ReportTable
            rows={outputs.insertion_order_metrics}
            columns={insertionOrderColumns}
            initialLimit={15}
          />
        </ReportSection>
      )}

      {outputs.line_item_metrics && outputs.line_item_metrics.length > 0 && (
        <ReportSection title="Line items" count={outputs.line_item_metrics.length}>
          <ReportTable rows={outputs.line_item_metrics} columns={lineItemColumns} initialLimit={15} />
        </ReportSection>
      )}

      {/* Performance broken down by line item (ranked top URLs per line item) — collapsed */}
      {outputs.top_urls_by_line_item && outputs.top_urls_by_line_item.length > 0 && (
        <ReportSection
          title="Performance by line item"
          subtitle="top URLs per line item"
          collapsible
          defaultOpen={false}
        >
          <ReportLineItemPerformance
            topUrls={outputs.top_urls_by_line_item}
            lineItems={outputs.line_item_metrics}
          />
        </ReportSection>
      )}

      {/* Lazy drill-down (large) */}
      {outputs.url_metrics && outputs.url_metrics.length > 0 && (
        <ReportSection
          title="URL drill-down"
          count={outputs.url_metrics.length}
          collapsible
          defaultOpen={false}
        >
          <ReportTable rows={outputs.url_metrics} columns={urlMetricColumns} initialLimit={25} />
        </ReportSection>
      )}

      {outputs.executions_data && outputs.executions_data.length > 0 && (
        <ReportSection
          title="Executions data"
          count={outputs.executions_data.length}
          collapsible
          defaultOpen={false}
        >
          <ReportExecutionsData rows={outputs.executions_data} />
        </ReportSection>
      )}
    </div>
  );
}

function CenteredState({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-24">{children}</div>
  );
}
